// src/components/FeeManagement/FeeTypeFormComponent.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import {
    Container,
    Typography,
    Grid,
    Box,
    Skeleton,
    CircularProgress,
    TextField,
    Button,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Slide,
    InputAdornment,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton
} from '@mui/material';
import { ThemeProvider, styled, alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TitleIcon from '@mui/icons-material/Title';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../../theme';

// --- Styled Components (Modern Theme) ---
const PageBackground = styled(Box)(({ theme }) => ({
    backgroundColor: 'transparent',
    minHeight: '100%',
    padding: theme.spacing(0.4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.2),
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 26,
    boxShadow: '0 18px 38px rgba(15, 23, 42, 0.05)',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
    background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.98)} 0%, ${alpha('#EFF6FF', 0.55)} 100%)`,
}));

const HeroStrip = styled(Box)(({ theme }) => ({
    borderRadius: 6,
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha('#EEF2FF', 0.95)} 0%, ${alpha('#FFFFFF', 1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
    marginBottom: theme.spacing(2.2),
}));

const MetricTile = styled(Box)(({ theme }) => ({
    borderRadius: 6,
    padding: theme.spacing(1.6),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
}));

const StyledTable = styled(Table)(({ theme }) => ({
    minWidth: 700,
    '& thead th': {
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : '#f8fafc',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '16px',
        borderBottom: `2px solid ${theme.palette.divider}`,
    },
    '& tbody tr': {
        transition: 'background-color 0.2s ease',
    },
    '& tbody tr:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.875rem',
    padding: '14px 16px',
    color: theme.palette.text.primary,
}));

const NoResultsContainer = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 6,
    border: `1px dashed ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease',
        '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
    },
}));

// --- Modern Dialog Styles ---
const DialogStyled = styled(Dialog)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(2),
            width: `calc(100% - ${theme.spacing(4)})`,
            maxWidth: '100%',
        },
    },
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(2.5, 3),
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    '&.MuiDialogContent-dividers': {
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
    }
}));

const DialogActionsStyled = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
}));

const DialogSlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const initialFeeTypeState = {
    feeTypeId: null,
    name: '',
    fees: '',
    schoolId: '',
    status: true,
};

// --- Main Component ---
const FeeTypeFormComponent = () => {
    const schoolId = useMemo(() => localStorage.getItem('schoolId'),[]);
    const hasSchoolId = !!schoolId;

    const [feeTypes, setFeeTypes] = useState([]);
    const [currentFeeType, setCurrentFeeType] = useState({ ...initialFeeTypeState, schoolId: schoolId || '' });
    const [loading, setLoading] = useState(true);
    const[isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const[itemToToggle, setItemToToggle] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const activeCount = useMemo(() => feeTypes.filter((type) => type.status).length, [feeTypes]);

    // --- Data Fetching ---
    const fetchFeeTypes = useCallback(async (showLoading = true) => {
        if (!hasSchoolId) {
            setError('School information is missing.');
            setFeeTypes([]);
            setLoading(false);
            return;
        }
        if (showLoading) setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/api/fees/types/school/${schoolId}`);
            setFeeTypes(response.data ||[]);
        } catch (err) {
            console.error("Fetch Fee Types Error:", err);
            const message = getApiErrorMessage(err, 'Failed to fetch fee types.');
            setError(message);
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [schoolId, hasSchoolId]);

    const fetchFeeTypeDetails = async (feeTypeIdToFetch) => {
        setIsSubmitting(true);
        try {
            const response = await axiosInstance.get(`/api/fees/types/${feeTypeIdToFetch}`);
            setCurrentFeeType({ ...response.data, fees: response.data.fees ?? '' });
            setIsEditMode(true);
            setFormErrors({});
            setDialogOpen(true);
        } catch (err) {
            console.error("Fetch Fee Type Details Error:", err);
            setSnackbar({ open: true, message: getApiErrorMessage(err, 'Error fetching fee type details.'), severity: 'error' });
            closeDialog();
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        fetchFeeTypes();
    }, [fetchFeeTypes]);

    // --- Event Handlers ---
    const handleDialogInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentFeeType((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!currentFeeType.name?.trim()) errors.name = 'Fee Type Name is required.';
        else if (currentFeeType.name.length > 100) errors.name = 'Name cannot exceed 100 characters.';

        const feesStr = String(currentFeeType.fees).trim();
        const feesNum = parseFloat(feesStr);
        if (feesStr === '' || feesStr === null) errors.fees = 'Fee Amount is required.';
        else if (isNaN(feesNum) || feesNum < 0) errors.fees = 'Enter a valid, non-negative amount.';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleDialogSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                ...currentFeeType,
                schoolId,
                fees: parseFloat(String(currentFeeType.fees).trim()),
            };
            if (!isEditMode) {
                delete payload.feeTypeId;
                delete payload.status;
            }

            if (isEditMode && currentFeeType.feeTypeId) {
                await axiosInstance.put(`/api/fees/types/${currentFeeType.feeTypeId}`, payload);
            } else {
                await axiosInstance.post('/api/fees/types', payload);
            }

            setSnackbar({ open: true, message: `Fee Type ${isEditMode ? 'updated' : 'created'} successfully`, severity: 'success' });
            closeDialog();
            fetchFeeTypes(false);
        } catch (err) {
            console.error("Save Fee Type Error:", err);
            setSnackbar({ open: true, message: getApiErrorMessage(err, 'Error saving fee type.'), severity: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const requestToggleStatus = (id, currentStatus, name) => {
        setItemToToggle({ id, currentStatus: currentStatus ?? false, name });
        setConfirmDialogOpen(true);
    };

    const performToggleStatus = async () => {
        if (!itemToToggle) return;
        const { id, currentStatus, name } = itemToToggle;
        const newStatus = !currentStatus;
        setConfirmDialogOpen(false);

        const originalFeeTypes = [...feeTypes];
        setFeeTypes((prev) => prev.map((type) => type.feeTypeId === id ? { ...type, status: newStatus } : type));

        try {
            await axiosInstance.put(`/api/fees/types/${id}/toggle-status`, null, { params: { activate: newStatus } });
            setSnackbar({ open: true, message: `Fee Type "${name}" ${newStatus ? 'activated' : 'deactivated'}`, severity: 'success' });
        } catch (err) {
            console.error("Toggle Status Error:", err);
            setFeeTypes(originalFeeTypes);
            setSnackbar({ open: true, message: getApiErrorMessage(err, `Error toggling status for "${name}"`), severity: 'error' });
        } finally {
            setItemToToggle(null);
        }
    };

    const openDialogForCreate = () => {
        setIsEditMode(false);
        setCurrentFeeType({ ...initialFeeTypeState, schoolId: schoolId || '' });
        setFormErrors({});
        setError(null);
        setDialogOpen(true);
    };

    const openDialogForEdit = (feeTypeId) => {
        fetchFeeTypeDetails(feeTypeId);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setTimeout(() => {
            setIsEditMode(false);
            setCurrentFeeType({ ...initialFeeTypeState, schoolId: schoolId || '' });
            setFormErrors({});
            setError(null);
        }, 150);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    if (!loading && !hasSchoolId) {
        return (
            <ThemeProvider theme={theme}>
                <PageBackground>
                    <Container maxWidth="lg" disableGutters>
                        <StyledCard>
                            <CardContent>
                                <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                                    School information is missing. Cannot manage fee types.
                                </Alert>
                            </CardContent>
                        </StyledCard>
                    </Container>
                </PageBackground>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <PageBackground>
                <Container maxWidth="lg" disableGutters>
                    <StyledCard elevation={0}>
                        <CardContent sx={{ pb: '16px !important', p: { xs: 2, sm: 3 } }}>
                            <HeroStrip>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={7}>
                                        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}>
                                            Fee Catalogue
                                        </Typography>
                                        <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800 }}>
                                            Keep your fee types clean, visible, and easy to maintain
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                                            Use this screen to maintain the building blocks of your fee system before mapping them to classes or terms.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={6}>
                                                <MetricTile>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                        Fee Types
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>
                                                        {feeTypes.length}
                                                    </Typography>
                                                </MetricTile>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MetricTile>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                        Active
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>
                                                        {activeCount}
                                                    </Typography>
                                                </MetricTile>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </HeroStrip>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                                <Typography variant="h6" fontWeight="600" color="text.primary">
                                    Manage Fee Types
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={openDialogForCreate}
                                    disabled={!hasSchoolId}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                                >
                                    Add Fee Type
                                </Button>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{ minHeight: '350px' }}>
                                {loading ? (
                                    <TableContainer sx={{ maxHeight: 600 }}>
                                        <StyledTable aria-label="loading fee types skeleton">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: '5%' }}>#</TableCell>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Fee Amount</TableCell>
                                                    <TableCell align="center">Status</TableCell>
                                                    <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[...Array(5)].map((_, index) => (
                                                    <TableRow key={index}>
                                                        <StyledTableCell><Skeleton variant="text" width={20} /></StyledTableCell>
                                                        <StyledTableCell><Skeleton variant="text" width="70%" /></StyledTableCell>
                                                        <StyledTableCell><Skeleton variant="text" width={70} /></StyledTableCell>
                                                        <StyledTableCell align="center"><Skeleton variant="circular" width={34} height={20} sx={{ mx: 'auto' }} /></StyledTableCell>
                                                        <StyledTableCell align="right"><Skeleton variant="rectangular" width={70} height={30} sx={{ ml: 'auto', borderRadius: '6px' }} /></StyledTableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </StyledTable>
                                    </TableContainer>
                                ) : error && !feeTypes.length ? (
                                    <NoResultsContainer>
                                        <WarningAmberIcon color="error" sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                                        <Typography color="error" variant="h6" fontWeight="600" gutterBottom>{error}</Typography>
                                        <Button variant="outlined" color="primary" onClick={() => fetchFeeTypes()} sx={{ mt: 2, borderRadius: '8px' }}>Retry</Button>
                                    </NoResultsContainer>
                                ) : !feeTypes.length ? (
                                    <NoResultsContainer>
                                        <AccountBalanceWalletIcon sx={{ fontSize: 50, color: 'text.disabled', mb: 2 }} />
                                        <Typography variant="h6" color="text.primary" fontWeight="600" gutterBottom>
                                            No Fee Types Found
                                        </Typography>
                                        <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                            Add your first fee type to categorize transactions.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={openDialogForCreate}
                                            disabled={!hasSchoolId}
                                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Add Fee Type
                                        </Button>
                                    </NoResultsContainer>
                                ) : (
                                    <TableContainer sx={{ maxHeight: 600 }}>
                                        <StyledTable stickyHeader aria-label="fee types table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: '5%' }}>#</TableCell>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Fee Amount</TableCell>
                                                    <TableCell align="center">Status</TableCell>
                                                    <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {feeTypes.map((type, index) => (
                                                    <TableRow hover key={type.feeTypeId}>
                                                        <StyledTableCell>
                                                            <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            <Typography variant="body2" fontWeight="600">{type.name}</Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            <Typography variant="body2" fontWeight="700" color="text.primary">
                                                                {type.fees != null ? `₹ ${Number(type.fees).toLocaleString('en-IN')}` : 'N/A'}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center">
                                                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                                <Chip 
                                                                    label={type.status ? 'Active' : 'Inactive'} 
                                                                    color={type.status ? 'success' : 'default'} 
                                                                    size="small" 
                                                                    variant={type.status ? 'filled' : 'outlined'}
                                                                    sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: '70px' }} 
                                                                />
                                                                <Switch
                                                                    checked={type.status ?? false}
                                                                    onChange={() => requestToggleStatus(type.feeTypeId, type.status, type.name)}
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                            </Box>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="right">
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                color="primary"
                                                                startIcon={<EditIcon fontSize="small" />}
                                                                onClick={() => openDialogForEdit(type.feeTypeId)}
                                                                sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </StyledTableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </StyledTable>
                                    </TableContainer>
                                )}
                            </Box>
                        </CardContent>
                    </StyledCard>

                    {/* Create / Edit Dialog */}
                    <DialogStyled
                        open={dialogOpen}
                        onClose={closeDialog}
                        TransitionComponent={DialogSlideTransition}
                        fullWidth
                        maxWidth="sm"
                        component="form"
                        onSubmit={handleDialogSubmit}
                        noValidate
                    >
                        <DialogTitleStyled>
                            {isEditMode ? 'Edit Fee Type' : 'Create New Fee Type'}
                            <IconButton onClick={closeDialog} size="small" sx={{ color: 'text.secondary' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitleStyled>
                        <DialogContentStyled dividers>
                            <Grid container spacing={3} sx={{ pt: 1 }}>
                                <Grid item xs={12}>
                                    <CustomTextField
                                        label="Fee Type Name"
                                        name="name"
                                        value={currentFeeType.name}
                                        onChange={handleDialogInputChange}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        disabled={isSubmitting}
                                        autoFocus
                                        error={!!formErrors.name}
                                        helperText={formErrors.name || ' '}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TitleIcon color={formErrors.name ? "error" : "action"} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomTextField
                                        label="Fee Amount"
                                        name="fees"
                                        value={currentFeeType.fees}
                                        onChange={handleDialogInputChange}
                                        fullWidth
                                        required
                                        type="number"
                                        variant="outlined"
                                        disabled={isSubmitting}
                                        error={!!formErrors.fees}
                                        helperText={formErrors.fees || ' '}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountBalanceWalletIcon color={formErrors.fees ? "error" : "action"} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            inputProps: { min: 0, step: "0.01" },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContentStyled>
                        <DialogActionsStyled>
                            <Button 
                                onClick={closeDialog} 
                                disabled={isSubmitting} 
                                color="inherit" 
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}
                            >
                                {isSubmitting ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                            </Button>
                        </DialogActionsStyled>
                    </DialogStyled>

                    {/* Status Toggle Confirmation Dialog */}
                    <DialogStyled
                        open={confirmDialogOpen}
                        onClose={() => setConfirmDialogOpen(false)}
                        maxWidth="xs"
                    >
                        <DialogTitleStyled>Confirm Status Change</DialogTitleStyled>
                        <DialogContentStyled>
                            <DialogContentText sx={{ color: 'text.primary', mt: 1 }}>
                                Are you sure you want to{' '}
                                <Box component="span" fontWeight="700" color={itemToToggle && !itemToToggle.currentStatus ? 'success.main' : 'error.main'}>
                                    {itemToToggle && !itemToToggle.currentStatus ? 'activate' : 'deactivate'}
                                </Box>
                                {' '}the fee type <strong>"{itemToToggle?.name || ''}"</strong>?
                            </DialogContentText>
                        </DialogContentStyled>
                        <DialogActionsStyled>
                            <Button 
                                onClick={() => setConfirmDialogOpen(false)} 
                                color="inherit" 
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={performToggleStatus}
                                variant="contained"
                                color={itemToToggle && !itemToToggle.currentStatus ? 'success' : 'error'}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}
                            >
                                Confirm
                            </Button>
                        </DialogActionsStyled>
                    </DialogStyled>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleSnackbarClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert 
                            onClose={handleSnackbarClose} 
                            severity={snackbar.severity} 
                            variant="filled" 
                            sx={{ width: '100%', borderRadius: '8px' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Container>
            </PageBackground>
        </ThemeProvider>
    );
};

export default FeeTypeFormComponent;
